/**
 * usePrefetch — Background data prefetcher for instant page transitions.
 * Fires once on app mount, staggers API calls to avoid overwhelming the backend.
 * Writes results to both SWR cache (localStorage `swr_cache:` prefix) and
 * page-specific cache keys used by individual pages.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Write to SWR-compatible localStorage cache (read by useApi.js) */
const cacheWrite = (path, data) => {
  try {
    const url = `${API_URL}${path}`;
    localStorage.setItem(`swr_cache:${url}`, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
};

/** Silent fetch with auth + SWR cache write */
const prefetch = async (path, token) => {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(`${API_URL}${path}`, { credentials: 'include', headers });
    if (!res.ok) return null;
    const data = await res.json();
    cacheWrite(path, data);
    return data;
  } catch { return null; }
};

/** Stagger fetches — resolves after `delayMs` then calls `fn` */
const stagger = (fn, delayMs) => new Promise(resolve => {
  setTimeout(async () => { await fn(); resolve(); }, delayMs);
});

export const usePrefetch = () => {
  const didRun = useRef(false);
  const { user } = useAuth();
  const { locationString } = useLocation();
  const token = typeof window !== 'undefined' ? localStorage.getItem('fq_auth_token') : null;

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const loc = encodeURIComponent(locationString || '');
    const hour = new Date().getHours();
    const vibe = hour < 11 ? 'morning' : hour < 15 ? 'lunch' : hour < 18 ? 'afternoon' : hour < 22 ? 'evening' : 'late-night';

    // Use requestIdleCallback to avoid blocking initial render
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 100));

    schedule(async () => {
      // ── Wave 1 (0ms): Core pages the user will likely visit first ──
      const surprisePromises = [
        prefetch(`/api/surprise-me?location=${loc}&mode=dine-out&vibe=${vibe}`, token),
        prefetch(`/api/surprise-me?location=${loc}&mode=order-in&vibe=${vibe}`, token),
        prefetch(`/api/surprise-me?location=${loc}&mode=cook-at-home&vibe=${vibe}`, token),
      ];

      const surpriseResults = await Promise.all(surprisePromises);

      // Also write to the page-specific cache key used by SurpriseMePage
      try {
        const mergedSections = {};
        surpriseResults.forEach(data => {
          if (data?.sections) Object.assign(mergedSections, data.sections);
        });
        if (Object.keys(mergedSections).length > 0) {
          localStorage.setItem('fq_surprise_sections', JSON.stringify(mergedSections));
        }
      } catch {}

      // Prefetch food images for the results
      const allDishes = surpriseResults
        .filter(Boolean)
        .flatMap(r => Object.values(r.sections || {}))
        .flat()
        .filter(Boolean);
      prefetchImages(allDishes);

      // ── Wave 2 (200ms): Discover + Food Mood ──
      await stagger(async () => {
        await Promise.all([
          prefetch('/api/discover-feed?tab=for_you&page=1&limit=10', token),
          prefetch('/api/food-mood/today', token),
          prefetch('/api/posts/trending-tags', token),
        ]);
      }, 200);

      // ── Wave 3 (500ms): Auth-required pages ──
      if (token) {
        await stagger(async () => {
          await Promise.all([
            prefetch('/api/auth/me', token),
            prefetch('/api/social/following-ids', token),
            prefetch('/api/notifications/unread-count', token),
            prefetch('/api/foodlist', token),
          ]);
        }, 300);

        // ── Wave 4 (900ms): Secondary pages ──
        await stagger(async () => {
          await Promise.all([
            prefetch('/api/taste-dna/profile', token),
            prefetch('/api/smart/chat/sessions', token),
            prefetch('/api/foodlist/collections', token),
            prefetch('/api/activity/summary', token),
          ]);
        }, 400);
      }

      // ── Wave 5 (1200ms): Home feed data ──
      await stagger(async () => {
        await Promise.all([
          prefetch('/api/posts/feed?page=1&limit=10', token),
          prefetch('/api/quests/daily', token),
        ]);

        // Home feed + foodie feed are POST requests — prefetch them too
        const locStr = localStorage.getItem('fq_location') || '';
        if (locStr) {
          try {
            const [feedRes, foodieRes] = await Promise.all([
              fetch(`${API_URL}/api/home/feed`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                credentials: 'include',
                body: JSON.stringify({ location: locStr, user_preferences: {} }),
              }),
              fetch(`${API_URL}/api/home/foodie-feed`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ location: locStr }),
              }),
            ]);
            const city = locStr.split(',')[0].trim().toLowerCase().replace(/\s/g, '_');
            if (feedRes.ok) {
              const feedData = await feedRes.json();
              try { localStorage.setItem(`home_feed_${city}`, JSON.stringify(feedData)); } catch {}
            }
            if (foodieRes.ok) {
              const foodieData = await foodieRes.json();
              try { localStorage.setItem(`home_foodie_${city}`, JSON.stringify(foodieData.posts || [])); } catch {}
            }
          } catch {}
        }
      }, 300);
    });
  }, [locationString, user]);
};

/** Preload food images into browser cache */
function prefetchImages(dishes) {
  if (!dishes.length) return;
  // Use the batch endpoint if available, otherwise preload individual images
  const uniqueImages = [...new Set(
    dishes
      .map(d => d?.image || d?.photo_url)
      .filter(Boolean)
  )].slice(0, 20); // Cap at 20 images

  uniqueImages.forEach(url => {
    const img = new Image();
    img.src = url;
  });
}

export default usePrefetch;
